import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { RemmiConfirmOptions, RemmiConfirmTone } from './remmi-confirm.model';

const DEFAULT_ICONS: Record<RemmiConfirmTone, string> = {
  danger: 'pi pi-trash',
  primary: 'pi pi-question-circle',
  warning: 'pi pi-exclamation-triangle',
};

const CLOSE_ANIMATION_MS = 140;
const VIEWPORT_GAP = 12;
const ANCHOR_GAP = 10;
const ARROW_INSET = 18;
/** Below this width the popover stays centred: an anchored card would cover most of a phone screen anyway. */
const MIN_ANCHOR_VIEWPORT = 480;

/**
 * The one confirmation prompt used across the app: a small popover pointing at the button that opened
 * it, or a centred card when there is no button to point at. Never placed in a template:
 * `RemmiConfirmService` creates it on `document.body` per call and destroys it once answered.
 */
@Component({
  selector: 'app-remmi-confirm',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './remmi-confirm.component.html',
  styleUrls: ['./remmi-confirm.component.scss'],
})
export class RemmiConfirmComponent implements AfterViewInit {
  @ViewChild('card') card: ElementRef<HTMLElement>;
  @ViewChild('arrow') arrow: ElementRef<HTMLElement>;
  @ViewChild('confirmBtn') confirmBtn: ElementRef<HTMLButtonElement>;

  options: RemmiConfirmOptions = {};
  /** Viewport rect of the element the popover points at; null centres it. */
  anchor: DOMRect = null;
  result: Subject<boolean>;
  isClosing = false;

  get isAnchored(): boolean {
    return !!this.anchor && window.innerWidth > MIN_ANCHOR_VIEWPORT;
  }

  get icon(): string {
    return this.options.icon ?? DEFAULT_ICONS[this.options.tone ?? 'danger'];
  }

  ngAfterViewInit() {
    if (this.isAnchored) {
      this.placeNearAnchor();
    }
    this.card.nativeElement.classList.add('rc-pop--ready');
    setTimeout(() => this.confirmBtn?.nativeElement.focus());
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.answer(false);
  }

  @HostListener('window:resize')
  onResize() {
    this.answer(false);
  }

  answer(isConfirmed: boolean) {
    if (this.isClosing) {
      return;
    }
    this.isClosing = true;
    setTimeout(() => {
      this.result.next(isConfirmed);
      this.result.complete();
    }, CLOSE_ANIMATION_MS);
  }

  /** Below the anchor when it fits, otherwise above; horizontally centred on it but kept inside the viewport. */
  private placeNearAnchor() {
    const card = this.card.nativeElement;
    const { offsetWidth: width, offsetHeight: height } = card;
    const anchorCenter = this.anchor.left + this.anchor.width / 2;

    const fitsBelow = this.anchor.bottom + ANCHOR_GAP + height <= window.innerHeight - VIEWPORT_GAP;
    const isAbove = !fitsBelow && this.anchor.top - ANCHOR_GAP - height >= VIEWPORT_GAP;
    const top = isAbove
      ? this.anchor.top - ANCHOR_GAP - height
      : Math.min(this.anchor.bottom + ANCHOR_GAP, window.innerHeight - VIEWPORT_GAP - height);
    const left = Math.min(Math.max(anchorCenter - width / 2, VIEWPORT_GAP), window.innerWidth - VIEWPORT_GAP - width);
    const arrowLeft = Math.min(Math.max(anchorCenter - left, ARROW_INSET), width - ARROW_INSET);

    card.style.top = `${Math.max(top, VIEWPORT_GAP)}px`;
    card.style.left = `${left}px`;
    card.style.transformOrigin = `${arrowLeft}px ${isAbove ? '100%' : '0'}`;
    card.classList.add(isAbove ? 'rc-pop--above' : 'rc-pop--below');
    this.arrow.nativeElement.style.left = `${arrowLeft}px`;
  }
}
